import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../contexts/CategoryContext';

/**
 * Category and optional subcategory selection.
 *
 * Two ways in: type to search across both levels (results read
 * "Kategorie → Subkategorie"), or pick a category and refine afterwards.
 * The subcategory is never mandatory — choosing a category commits the value
 * immediately with subcategoryId = null.
 */
const CategoryPicker = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const { byCode, flatOptions, loading } = useCategories();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedCode, setExpandedCode] = useState(null);
  const [highlight, setHighlight] = useState(0);
  const searchRef = useRef(null);

  const selectedCategory = value.categoryCode ? byCode[value.categoryCode] : null;
  const selectedSubcategory = useMemo(() => {
    if (!selectedCategory || !value.subcategoryId) return null;
    return (selectedCategory.subcategories || []).find((s) => s.id === value.subcategoryId) || null;
  }, [selectedCategory, value.subcategoryId]);

  // An archived category stays visible as the current value instead of
  // silently resetting the expense. byCode covers archived rows too, so the
  // check is on the flag, not on the lookup coming up empty; a code with no
  // row at all (older state, deleted category) counts as archived as well.
  const isArchivedValue =
    Boolean(value.categoryCode) && (!selectedCategory || !selectedCategory.is_active);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const searchResults = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return flatOptions.filter(
      (option) =>
        option.categoryName.toLowerCase().includes(term) ||
        (option.subcategoryName || '').toLowerCase().includes(term)
    );
  }, [search, flatOptions]);

  const categoryResults = useMemo(
    () => flatOptions.filter((option) => option.subcategoryId === null),
    [flatOptions]
  );

  const visibleOptions = search.trim() ? searchResults : categoryResults;

  useEffect(() => {
    setHighlight(0);
  }, [search, open]);

  const commit = (categoryCode, subcategoryId) => {
    onChange({ categoryCode, subcategoryId: subcategoryId ?? null });
  };

  const close = () => {
    setOpen(false);
    setSearch('');
    setExpandedCode(null);
  };

  const chooseOption = (option) => {
    commit(option.categoryCode, option.subcategoryId);
    if (option.subcategoryId === null) {
      const category = byCode[option.categoryCode];
      const hasSubcategories = (category?.subcategories || []).length > 0;
      if (hasSubcategories && !search.trim()) {
        // Stay open so the optional second level can be picked right away.
        setExpandedCode(option.categoryCode);
        return;
      }
    }
    close();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((current) => Math.min(current + 1, visibleOptions.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const option = visibleOptions[highlight];
      if (option) chooseOption(option);
    }
  };

  const triggerLabel = () => {
    if (isArchivedValue) {
      const name = selectedCategory ? selectedCategory.name : value.categoryCode;
      return t('categoryManagement.picker.archivedValue', { name });
    }
    if (!selectedCategory) return t('categoryManagement.picker.choose');
    const icon = selectedCategory.icon ? `${selectedCategory.icon} ` : '';
    if (selectedSubcategory) {
      const subIcon = selectedSubcategory.icon ? `${selectedSubcategory.icon} ` : '';
      return `${icon}${selectedCategory.name} → ${subIcon}${selectedSubcategory.name}`;
    }
    return `${icon}${selectedCategory.name}`;
  };

  const expanded = expandedCode ? byCode[expandedCode] : null;

  return (
    <div className="relative">
      <button
        type="button"
        className="miko-input w-full text-left"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled || loading}
      >
        {loading ? t('categoryManagement.picker.loading') : triggerLabel()}
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="p-2">
            <input
              ref={searchRef}
              type="text"
              aria-label={t('categoryManagement.picker.search')}
              className="miko-input w-full"
              placeholder={t('categoryManagement.picker.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <ul className="max-h-64 overflow-y-auto py-1">
            {visibleOptions.map((option, index) => (
              <li key={option.key}>
                <button
                  type="button"
                  className={`w-full px-3 py-2 text-left ${
                    index === highlight ? 'bg-gray-100 dark:bg-gray-700' : ''
                  }`}
                  onClick={() => chooseOption(option)}
                >
                  {/* Icon and label are separate nodes on purpose: the label
                      must stay findable on its own, by tests and by search. */}
                  {option.categoryIcon && <span aria-hidden="true">{option.categoryIcon} </span>}
                  <span>{option.label}</span>
                </button>
              </li>
            ))}
            {visibleOptions.length === 0 && (
              <li className="px-3 py-2 text-gray-500">{t('categoryManagement.picker.noMatches')}</li>
            )}
          </ul>

          {expanded && (expanded.subcategories || []).length > 0 && (
            <div className="border-t border-gray-200 py-1 dark:border-gray-700">
              <button
                type="button"
                className="w-full px-3 py-2 text-left"
                onClick={() => {
                  commit(expanded.code, null);
                  close();
                }}
              >
                {t('categoryManagement.picker.noSubcategory')}
              </button>
              {expanded.subcategories.map((subcategory) => (
                <button
                  key={subcategory.id}
                  type="button"
                  className="w-full px-3 py-2 text-left"
                  onClick={() => {
                    commit(expanded.code, subcategory.id);
                    close();
                  }}
                >
                  {subcategory.icon && <span aria-hidden="true">{subcategory.icon} </span>}
                  <span>{subcategory.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryPicker;
